import { ErrorApi } from '../identidad/cliente-identidad.js'
import { notificar } from '../fundacion/alertas.js'
import {
  actualizarInstitucion,
  actualizarJornada,
  actualizarPeriodo,
  actualizarPrograma,
  actualizarSede,
  actualizarSedeDatos,
  cerrarPeriodo,
  crearInstitucion,
  crearJornada,
  crearPeriodo,
  crearPrograma,
  crearSede,
  listarInstituciones,
  listarJornadas,
  listarPeriodos,
  listarProgramas,
  listarSedes,
  type EstadoCatalogo,
  type Institucion,
  type Jornada,
  type Periodo,
  type Programa,
  type Sede,
} from './cliente-catalogos.js'

function elemento<T extends Element>(selector: string): T {
  const encontrado = document.querySelector<T>(selector)
  if (!encontrado) throw new Error(`No existe el elemento requerido: ${selector}`)
  return encontrado
}

const alerta = elemento<HTMLElement>('#alerta-catalogos')
let programas: Programa[] = []
let periodos: Periodo[] = []
let instituciones: Institucion[] = []
let sedes: Sede[] = []
let jornadas: Jornada[] = []
let programaEditando: Programa | null = null
let periodoEditando: Periodo | null = null
let institucionEditando: Institucion | null = null
let sedeEditando: Sede | null = null
let jornadaEditando: Jornada | null = null

function informar(mensaje: string, tipo = 'success'): void {
  alerta.className = 'alert d-none'
  notificar(mensaje, tipo === 'danger' ? 'error' : (tipo as 'success' | 'warning' | 'info'))
}

function informarError(error: unknown): void {
  informar(
    error instanceof ErrorApi ? error.message : 'No fue posible completar la operación.',
    'danger',
  )
}

function celda(texto: string): HTMLTableCellElement {
  const creada = document.createElement('td')
  creada.textContent = texto
  return creada
}

function botonAccion(
  texto: string,
  accion: () => Promise<unknown>,
  clase = 'outline-primary',
): HTMLButtonElement {
  const boton = document.createElement('button')
  boton.type = 'button'
  boton.className = `btn btn-sm btn-${clase}`
  boton.textContent = texto
  boton.addEventListener('click', () => {
    boton.disabled = true
    void accion()
      .then(() => cargarCatalogos('Cambio guardado correctamente.'))
      .catch(informarError)
      .finally(() => {
        boton.disabled = false
      })
  })
  return boton
}

function botonEditar(accion: () => void): HTMLButtonElement {
  const boton = document.createElement('button')
  boton.type = 'button'
  boton.className = 'btn btn-sm btn-outline-secondary ms-1'
  boton.textContent = 'Editar'
  boton.addEventListener('click', accion)
  return boton
}

function estadoAlterno(estado: EstadoCatalogo): EstadoCatalogo {
  return estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO'
}

function renderizarProgramas(): void {
  const cuerpo = elemento<HTMLTableSectionElement>('#tabla-programas')
  cuerpo.replaceChildren(
    ...programas.map((dato) => {
      const fila = document.createElement('tr')
      fila.append(celda(dato.codigo), celda(dato.nombre), celda(dato.estado))
      const acciones = celda('')
      acciones.append(botonEditar(() => prepararEdicionPrograma(dato)))
      acciones.append(
        botonAccion(dato.estado === 'ACTIVO' ? 'Inactivar' : 'Activar', () =>
          actualizarPrograma(dato, estadoAlterno(dato.estado)),
        ),
      )
      fila.append(acciones)
      return fila
    }),
  )
}

function renderizarPeriodos(): void {
  const cuerpo = elemento<HTMLTableSectionElement>('#tabla-periodos')
  cuerpo.replaceChildren(
    ...periodos.map((dato) => {
      const fila = document.createElement('tr')
      fila.append(
        celda(dato.codigo),
        celda(`${dato.fechaInicio} a ${dato.fechaFin}`),
        celda(dato.estado),
      )
      const acciones = celda('')
      if (dato.estado === 'ACTIVO') {
        acciones.append(botonEditar(() => prepararEdicionPeriodo(dato)))
        acciones.append(botonAccion('Cerrar', () => cerrarPeriodo(dato), 'warning'))
      }
      fila.append(acciones)
      return fila
    }),
  )
}

function renderizarInstituciones(): void {
  const cuerpo = elemento<HTMLTableSectionElement>('#tabla-instituciones')
  cuerpo.replaceChildren(
    ...instituciones.map((dato) => {
      const fila = document.createElement('tr')
      fila.append(
        celda(dato.nombre),
        celda(dato.nit),
        celda(`${dato.correo} · ${dato.celular}`),
        celda(dato.estado),
      )
      const acciones = celda('')
      acciones.append(botonEditar(() => prepararEdicionInstitucion(dato)))
      acciones.append(
        botonAccion(dato.estado === 'ACTIVO' ? 'Inactivar' : 'Activar', () =>
          actualizarInstitucion(dato, estadoAlterno(dato.estado)),
        ),
      )
      fila.append(acciones)
      return fila
    }),
  )
}

function renderizarSedesJornadas(): void {
  const cuerpo = elemento<HTMLTableSectionElement>('#tabla-sedes-jornadas')
  const filas: HTMLTableRowElement[] = []
  for (const sede of sedes) {
    const jornadasSede = jornadas.filter(({ sedeId }) => sedeId === sede.id)
    const registros = jornadasSede.length ? jornadasSede : [null]
    for (const jornada of registros) {
      const fila = document.createElement('tr')
      const institucion = instituciones.find(({ id }) => id === sede.institucionId)
      fila.append(
        celda(institucion?.nombre ?? sede.institucionNombre ?? 'Institución no disponible'),
        celda(sede.nombre),
        celda(jornada ? (jornada.nombre === 'MANANA' ? 'Mañana' : 'Tarde') : 'Sin jornada'),
        celda(jornada?.estado ?? sede.estado),
      )
      const acciones = celda('')
      acciones.append(botonEditar(() => prepararEdicionSede(sede)))
      acciones.append(
        botonAccion(sede.estado === 'ACTIVO' ? 'Inactivar sede' : 'Activar sede', () =>
          actualizarSede(sede, estadoAlterno(sede.estado)),
        ),
      )
      if (jornada) {
        acciones.append(botonEditar(() => prepararEdicionJornada(jornada)))
        acciones.append(
          botonAccion(jornada.estado === 'ACTIVO' ? 'Inactivar jornada' : 'Activar jornada', () =>
            actualizarJornada(jornada, estadoAlterno(jornada.estado)),
          ),
        )
      }
      fila.append(acciones)
      filas.push(fila)
    }
  }
  cuerpo.replaceChildren(...filas)
}

function opcion(valor: string, texto: string): HTMLOptionElement {
  const creada = document.createElement('option')
  creada.value = valor
  creada.textContent = texto
  return creada
}

function activarEdicion(etiqueta: string, cancelar: () => void): void {
  elemento<HTMLButtonElement>('#boton-guardar-sede-jornada').textContent = etiqueta
  elemento<HTMLButtonElement>('#boton-cancelar-sede-jornada').classList.remove('d-none')
  elemento<HTMLButtonElement>('#boton-cancelar-sede-jornada').onclick = cancelar
}

function prepararEdicionPrograma(dato: Programa): void {
  programaEditando = dato
  elemento<HTMLInputElement>('#codigo-programa').value = dato.codigo
  elemento<HTMLInputElement>('#nombre-programa').value = dato.nombre
  const boton = elemento<HTMLButtonElement>('#formulario-programa button[type="submit"]')
  boton.textContent = 'Guardar cambios'
}

function prepararEdicionPeriodo(dato: Periodo): void {
  periodoEditando = dato
  elemento<HTMLInputElement>('#codigo-periodo').value = dato.codigo
  elemento<HTMLInputElement>('#inicio-periodo').value = dato.fechaInicio
  elemento<HTMLInputElement>('#fin-periodo').value = dato.fechaFin
  elemento<HTMLButtonElement>('#formulario-periodo button[type="submit"]').textContent =
    'Guardar cambios'
}

function prepararEdicionInstitucion(dato: Institucion): void {
  institucionEditando = dato
  elemento<HTMLInputElement>('#nombre-institucion').value = dato.nombre
  elemento<HTMLInputElement>('#nit-institucion').value = dato.nit
  elemento<HTMLInputElement>('#correo-institucion').value = dato.correo
  elemento<HTMLInputElement>('#celular-institucion').value = dato.celular
  elemento<HTMLInputElement>('#direccion-institucion').value = dato.direccion
  elemento<HTMLButtonElement>('#formulario-institucion button[type="submit"]').textContent =
    'Guardar cambios'
}

function prepararEdicionSede(dato: Sede): void {
  sedeEditando = dato
  jornadaEditando = null
  elemento<HTMLSelectElement>('#institucion-sede').value = dato.institucionId
  elemento<HTMLInputElement>('#nombre-sede').value = dato.nombre
  elemento<HTMLInputElement>('#direccion-sede').value = dato.direccion
  elemento<HTMLInputElement>('#celular-sede').value = dato.celular
  activarEdicion('Guardar sede', cancelarEdicionSedeJornada)
}

function prepararEdicionJornada(dato: Jornada): void {
  jornadaEditando = dato
  sedeEditando = sedes.find(({ id }) => id === dato.sedeId) ?? null
  if (sedeEditando) {
    elemento<HTMLSelectElement>('#institucion-sede').value = sedeEditando.institucionId
    elemento<HTMLInputElement>('#nombre-sede').value = sedeEditando.nombre
    elemento<HTMLInputElement>('#direccion-sede').value = sedeEditando.direccion
    elemento<HTMLInputElement>('#celular-sede').value = sedeEditando.celular
  }
  elemento<HTMLSelectElement>('#nombre-jornada').value = dato.nombre
  activarEdicion('Guardar jornada', cancelarEdicionSedeJornada)
}

function cancelarEdicionSedeJornada(): void {
  sedeEditando = null
  jornadaEditando = null
  elemento<HTMLFormElement>('#formulario-sede-jornada').reset()
  elemento<HTMLButtonElement>('#boton-guardar-sede-jornada').textContent =
    'Registrar sede y jornada'
  elemento<HTMLButtonElement>('#boton-cancelar-sede-jornada').classList.add('d-none')
}

function renderizarSelectores(): void {
  const selectorInstitucion = elemento<HTMLSelectElement>('#institucion-sede')
  selectorInstitucion.replaceChildren(
    opcion('', 'Seleccione…'),
    ...instituciones
      .filter(({ estado }) => estado === 'ACTIVO')
      .map(({ id, nombre }) => opcion(id, nombre)),
  )
}

export async function cargarCatalogos(mensaje?: string): Promise<void> {
  const resultados = await Promise.all([
    listarProgramas(),
    listarPeriodos(),
    listarInstituciones(),
    listarSedes(),
    listarJornadas(),
  ])
  ;[programas, periodos, instituciones, sedes, jornadas] = resultados.map(({ datos }) => datos) as [
    Programa[],
    Periodo[],
    Institucion[],
    Sede[],
    Jornada[],
  ]
  renderizarProgramas()
  renderizarPeriodos()
  renderizarInstituciones()
  renderizarSedesJornadas()
  renderizarSelectores()
  if (mensaje) informar(mensaje)
}

function manejarFormulario(formulario: string, operacion: () => Promise<unknown>): void {
  elemento<HTMLFormElement>(formulario).addEventListener('submit', (evento) => {
    evento.preventDefault()
    const forma = evento.currentTarget as HTMLFormElement
    forma.classList.add('was-validated')
    if (!forma.checkValidity()) return
    void operacion()
      .then(() => {
        forma.reset()
        forma.classList.remove('was-validated')
        if (formulario === '#formulario-programa') {
          programaEditando = null
          elemento<HTMLButtonElement>('#formulario-programa button[type="submit"]').textContent =
            'Crear programa'
        }
        if (formulario === '#formulario-periodo') {
          periodoEditando = null
          elemento<HTMLButtonElement>('#formulario-periodo button[type="submit"]').textContent =
            'Crear periodo'
        }
        if (formulario === '#formulario-institucion') {
          institucionEditando = null
          elemento<HTMLButtonElement>('#formulario-institucion button[type="submit"]').textContent =
            'Crear institución'
        }
        return cargarCatalogos('Registro creado correctamente.')
      })
      .catch(informarError)
  })
}

export function prepararInterfazCatalogos(): void {
  manejarFormulario('#formulario-programa', () =>
    programaEditando
      ? actualizarPrograma(programaEditando, {
          codigo: elemento<HTMLInputElement>('#codigo-programa').value.toUpperCase(),
          nombre: elemento<HTMLInputElement>('#nombre-programa').value,
        })
      : crearPrograma({
          codigo: elemento<HTMLInputElement>('#codigo-programa').value.toUpperCase(),
          nombre: elemento<HTMLInputElement>('#nombre-programa').value,
        }),
  )
  manejarFormulario('#formulario-periodo', () =>
    periodoEditando
      ? actualizarPeriodo(periodoEditando, {
          codigo: elemento<HTMLInputElement>('#codigo-periodo').value,
          fechaInicio: elemento<HTMLInputElement>('#inicio-periodo').value,
          fechaFin: elemento<HTMLInputElement>('#fin-periodo').value,
        })
      : crearPeriodo({
          codigo: elemento<HTMLInputElement>('#codigo-periodo').value,
          fechaInicio: elemento<HTMLInputElement>('#inicio-periodo').value,
          fechaFin: elemento<HTMLInputElement>('#fin-periodo').value,
        }),
  )
  manejarFormulario('#formulario-institucion', () =>
    institucionEditando
      ? actualizarInstitucion(institucionEditando, {
          nombre: elemento<HTMLInputElement>('#nombre-institucion').value,
          nit: elemento<HTMLInputElement>('#nit-institucion').value,
          correo: elemento<HTMLInputElement>('#correo-institucion').value,
          celular: elemento<HTMLInputElement>('#celular-institucion').value,
          direccion: elemento<HTMLInputElement>('#direccion-institucion').value,
        })
      : crearInstitucion({
          nombre: elemento<HTMLInputElement>('#nombre-institucion').value,
          nit: elemento<HTMLInputElement>('#nit-institucion').value,
          correo: elemento<HTMLInputElement>('#correo-institucion').value,
          celular: elemento<HTMLInputElement>('#celular-institucion').value,
          direccion: elemento<HTMLInputElement>('#direccion-institucion').value,
        }),
  )
  elemento<HTMLFormElement>('#formulario-sede-jornada').addEventListener('submit', (evento) => {
    evento.preventDefault()
    const forma = evento.currentTarget as HTMLFormElement
    forma.classList.add('was-validated')
    if (!forma.checkValidity()) return
    const datosSede = {
      institucionId: elemento<HTMLSelectElement>('#institucion-sede').value,
      nombre: elemento<HTMLInputElement>('#nombre-sede').value,
      direccion: elemento<HTMLInputElement>('#direccion-sede').value,
      celular: elemento<HTMLInputElement>('#celular-sede').value,
    }
    const nombreJornada = elemento<HTMLSelectElement>('#nombre-jornada').value as 'MANANA' | 'TARDE'
    const operacion = sedeEditando
      ? actualizarSedeDatos(sedeEditando, datosSede).then((sede) =>
          jornadaEditando
            ? actualizarJornada(jornadaEditando, { nombre: nombreJornada })
            : crearJornada({ sedeId: sede.id, nombre: nombreJornada }),
        )
      : crearSede(datosSede).then((sede) =>
          crearJornada({ sedeId: sede.id, nombre: nombreJornada }),
        )
    void operacion
      .then(() => {
        cancelarEdicionSedeJornada()
        forma.classList.remove('was-validated')
        return cargarCatalogos('Sede y jornada guardadas correctamente.')
      })
      .catch(informarError)
  })
}
